import { z } from 'zod';

/**
 * Cite-checking (ADR-036): the citations in a pasted draft are checked against the library
 * without AI. "Not found" always means not found within the library, never that the law or
 * the case does not exist. The draft is not stored and not sent to an AI model.
 */

export const CITE_CHECK_TEXT_MAX = 30_000;
export const CITE_CHECK_DAILY_LIMIT = 20;

export const CiteCheckRequestSchema = z
  .object({ text: z.string().trim().min(10).max(CITE_CHECK_TEXT_MAX) })
  .strict();

export const CiteCheckStatusSchema = z.enum([
  /** Matched to one document in the library. */
  'found',
  /** A close match only (e.g. an amending Act of the same name and year): check it. */
  'probable',
  /** The citation belongs to a judgment in the library with different party names. */
  'mismatch',
  /** Not found within the library. */
  'not_found',
  /** A kind of citation the library cannot check (e.g. a law report). */
  'not_checked',
]);
export type CiteCheckStatus = z.infer<typeof CiteCheckStatusSchema>;

export const CiteCheckDocumentSchema = z.object({
  versionId: z.string(),
  collection: z.enum(['acts', 'cases']),
  title: z.string(),
  year: z.number().int().nullable(),
});
export type CiteCheckDocument = z.infer<typeof CiteCheckDocumentSchema>;

export const CiteCheckQuoteSchema = z.object({
  text: z.string(),
  /**
   * `matches`: the words appear as quoted. `likely`: most of the wording appears, but the
   * extracted text is out of order or garbled there. `differs`: some of the wording appears.
   * `not_found`: the wording was not found in the document's extracted text.
   */
  status: z.enum(['matches', 'likely', 'differs', 'not_found']),
  pageStart: z.number().int().nullable(),
  closest: z.string().nullable(),
});
export type CiteCheckQuote = z.infer<typeof CiteCheckQuoteSchema>;

export const CiteCheckItemSchema = z.object({
  id: z.number().int(),
  kind: z.enum(['case', 'act', 'section', 'law_report']),
  /** The citation as written in the draft. */
  cited: z.string(),
  /** How many times it appears in the draft. */
  occurrences: z.number().int(),
  status: CiteCheckStatusSchema,
  match: CiteCheckDocumentSchema.nullable(),
  candidates: z.array(CiteCheckDocumentSchema),
  /** Sections: whether the section heading was found in the extracted text, and where. */
  section: z
    .object({
      number: z.string(),
      located: z.boolean(),
      pageStart: z.number().int().nullable(),
      excerpt: z.string().nullable(),
    })
    .nullable(),
  quotes: z.array(CiteCheckQuoteSchema),
  /** Acts: later Acts of the same name in the library, to check for amendment or repeal. */
  laterActs: z.array(CiteCheckDocumentSchema),
  /** Cases: how many other judgments in the library name its parties. */
  citedBy: z.number().int().nullable(),
  note: z.string(),
});
export type CiteCheckItem = z.infer<typeof CiteCheckItemSchema>;

export const CiteCheckResultSchema = z.object({
  items: z.array(CiteCheckItemSchema),
  counts: z.partialRecord(CiteCheckStatusSchema, z.number().int()),
  library: z.object({ acts: z.number().int(), cases: z.number().int() }),
  remainingToday: z.number().int(),
});
export type CiteCheckResult = z.infer<typeof CiteCheckResultSchema>;
